import { mentionMixin } from '../shared/mention-mixin';
import { insertNodeAtCursor, placeCursorAtEnd } from '../../util/contenteditable-helpers';
import { createEmojiPicker } from './emoji-picker';
import { voiceRecorderMixin } from './voice-recorder';

const ALT_TEXT_MAX_LENGTH = 120;

// A composer holding only whitespace or a stray <br> is empty to the user, so
// it isn't worth saving as a draft.
function htmlIsBlank(html) {
  const el = document.createElement('div');
  el.innerHTML = html ?? '';
  return el.textContent.trim() === '' && !el.querySelector('.chat-mention-chip');
}

// One line for the conversation list. An attachment with no caption still
// counts as a draft, so fall back to its file name.
function draftPreviewText(html, attachment) {
  const el = document.createElement('div');
  el.innerHTML = html ?? '';
  return el.textContent.trim() || attachment?.selectedFile?.name || '';
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const FILE_LIMITS = {
  image: { maxSize: 10 * 1024 * 1024, types: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] },
  video: { maxSize: 50 * 1024 * 1024, types: ['video/mp4', 'video/quicktime', 'video/webm'] },
  audio: { maxSize: 20 * 1024 * 1024, types: ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/mpeg'] },
  file: {
    maxSize: 20 * 1024 * 1024,
    types: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ]
  }
};

function getFileCategory(contentType) {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';
  return 'file';
}

// The chip's class and dataset are the contract serializeContent() reads back,
// so every chip — typed or prefilled from an existing message — is built here.
function createMentionChip({ id, attributes: { uuid, name } }) {
  const chip = document.createElement('span');
  chip.className = 'chat-mention-chip';
  chip.setAttribute('contenteditable', 'false');
  Object.assign(chip.dataset, { uuid, name, userId: id });
  chip.textContent = `@${name}`;
  return chip;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function messageInput({ altTextErrorMessages = {}, recorderErrorMessages = {} } = {}) {
  return {
    ...mentionMixin(),
    ...voiceRecorderMixin({ recorderErrorMessages }),
    content: '',
    isSending: false,
    // Pre-translated strings passed from the template (JS i18n has a smaller
    // catalogue than the server, so error copy is rendered server-side).
    altTextErrorMessages,

    init() {
      this.$nextTick(() => {
        const el = this.$refs.messageTextarea;
        this._contentObserver = new MutationObserver(() => {
          this.content = el.textContent;
        });
        this._contentObserver.observe(el, { childList: true, subtree: true, characterData: true });
      });
    },

    destroy() {
      this._contentObserver?.disconnect();
      this.destroyVoiceRecorder();
    },

    selectedFile: null,
    previewUrl: null,
    fileError: null,
    // "Add image" modal — captures an accessibility description before an
    // image attachment lands in the composer preview.
    altTextModalOpen: false,
    imageAltText: '',
    imageAltTextFr: '',
    isGeneratingAltText: false,
    altTextError: null,
    altTextRequestId: 0,
    altTextMaxLength: ALT_TEXT_MAX_LENGTH,
    emojiPickerOpen: false,
    _pickerEl: null,
    replyTo: null,
    // Composer draft stashed while edit mode borrows the textarea; null when not editing.
    editDraftHtml: null,
    // Which conversation the textarea's current contents belong to. Lags
    // activeConversationId by one switch: the active conversation is already
    // the incoming one by the time the composer is told to stash.
    draftConversationId: null,

    clearReply() {
      this.replyTo = null;
    },

    // Swap the composer over to another conversation: keep what was typed for
    // the one being left, then show whatever that conversation had saved.
    handleConversationChanged(conversationId) {
      const el = this.$refs.messageTextarea;
      // Mid-edit the textarea holds the message being edited, so the text
      // worth keeping is the draft stashed when edit mode opened.
      const outgoing = this.editDraftHtml !== null ? this.editDraftHtml : (el?.innerHTML ?? '');

      this.replyTo = null;
      this.editDraftHtml = null;
      this.cancelRecording();

      this.stashDraft(outgoing);
      this.restoreDraft(conversationId);
    },

    stashDraft(html) {
      if (this.draftConversationId === null) return;

      const attachment = this.selectedFile
        ? {
          selectedFile: this.selectedFile,
          previewUrl: this.previewUrl,
          imageAltText: this.imageAltText,
          imageAltTextFr: this.imageAltTextFr
        }
        : null;

      if (htmlIsBlank(html) && !attachment) {
        this.discardDraft(this.draftConversationId);
      } else {
        this.$store.chat.setDraft(this.draftConversationId, {
          html,
          mentionedUsers: { ...this.mentionedUsers },
          attachment,
          previewText: draftPreviewText(html, attachment)
        });
      }
    },

    restoreDraft(conversationId) {
      const el = this.$refs.messageTextarea;
      const draft = conversationId === null ? null : this.$store.chat.drafts[conversationId];

      if (el) el.innerHTML = draft ? draft.html : '';
      this.mentionedUsers = draft ? { ...draft.mentionedUsers } : {};
      this.content = el ? el.textContent : '';
      this.applyAttachment(draft?.attachment ?? null);
      this.draftConversationId = conversationId;
    },

    // Assigns rather than calling clearFile, which revokes the preview URL —
    // that object URL now belongs to whichever draft holds it.
    applyAttachment(attachment) {
      this.selectedFile = attachment?.selectedFile ?? null;
      this.previewUrl = attachment?.previewUrl ?? null;
      this.imageAltText = attachment?.imageAltText ?? '';
      this.imageAltTextFr = attachment?.imageAltTextFr ?? '';
      this.fileError = null;
      this.altTextError = null;
      this.altTextModalOpen = false;
      // Invalidate any alt-text request still in flight so its response can't
      // land on the conversation we just switched to.
      this.altTextRequestId += 1;
      this.isGeneratingAltText = false;
      const fileInput = this.$refs.fileInput;
      if (fileInput) fileInput.value = '';
    },

    discardDraft(conversationId) {
      const url = this.$store.chat.drafts[conversationId]?.attachment?.previewUrl;
      if (url && url !== this.previewUrl) URL.revokeObjectURL(url);
      this.$store.chat.setDraft(conversationId, null);
    },

    // ─────────────────────────────────────────
    // Edit mode (prefill / confirm / cancel)
    // ─────────────────────────────────────────
    startEdit(message) {
      const el = this.$refs.messageTextarea;
      // Only stash on the first open — switching edit target A→B must not
      // clobber the draft saved when edit mode was entered.
      if (this.editDraftHtml === null) this.editDraftHtml = el.innerHTML;

      this.closeEmojiPicker();
      this.clearFile();
      this.replyTo = null;

      el.innerHTML = '';
      this.populateFromMessage(message, el);
      this.content = el.textContent;
      this.$nextTick(() => {
        el.focus();
        placeCursorAtEnd(el);
      });
    },

    // Rebuild the contenteditable DOM from the message's original-language text:
    // edits are written to the field the message was authored in (the other field
    // only ever holds a machine translation), so that's the text to prefill.
    // @{{uuid}} tokens become mention chips, newlines become <br>.
    populateFromMessage(message, el) {
      const text = message.original_lang === 'fr'
        ? (message.content_fr || message.content || '')
        : (message.content || message.content_fr || '');
      const mentions = message.mentions || [];

      const appendText = (chunk) => {
        chunk.split('\n').forEach((line, i) => {
          if (i > 0) el.appendChild(document.createElement('br'));
          if (line) el.appendChild(document.createTextNode(line));
        });
      };

      // Splitting on a capturing group alternates literal text (even indices)
      // with captured uuids (odd), so no cursor bookkeeping is needed.
      text.split(/@\{\{([\w-]+)\}\}/).forEach((part, i) => {
        if (i % 2 === 0) return appendText(part);

        const mention = mentions.find((m) => m.attributes.uuid === part);
        if (!mention) {
          // Unresolved mention (e.g. deactivated user) — keep the raw token so
          // the content round-trips unchanged.
          return appendText(`@{{${part}}}`);
        }

        el.appendChild(createMentionChip(mention));
        this.addMentionedUser(mention);
      });

      // A trailing text node guarantees the cursor has somewhere to land when
      // the message ends with a chip or <br>.
      el.appendChild(document.createTextNode(''));
    },

    async submitEdit() {
      if (!this.canSubmit) return;

      this.dismissMentionSearchResults();
      this.closeEmojiPicker();

      const { content } = this.serializeContent();
      if (content) await this.submitEditMessage(content);

      this.finishEdit();
    },

    cancelEdit() {
      this.cancelEditMessage();
      this.finishEdit();
    },

    // Restores the composer to its pre-edit state (including any draft the
    // user had typed before opening edit mode).
    finishEdit() {
      const el = this.$refs.messageTextarea;
      if (!el) return;

      el.innerHTML = this.editDraftHtml ?? '';
      this.editDraftHtml = null;
      this.content = el.textContent;
      this.$nextTick(() => el.focus());
    },

    get canSend() {
      return (this.content.trim().length > 0 || this.selectedFile) && !this.isSending;
    },

    // The composer's confirm control drives either a send or an edit; keeping
    // both rules here means the template just asks "can I submit?".
    get canSubmit() {
      return this.editingMessage
        ? this.content.trim().length > 0 && !this.isSavingEdit
        : this.canSend;
    },

    submitComposer() {
      if (this.editingMessage) {
        this.submitEdit();
      } else {
        this.sendMessage();
      }
    },

    get fileCategory() {
      if (!this.selectedFile) return null;
      return getFileCategory(this.selectedFile.type);
    },

    get formattedFileSize() {
      if (!this.selectedFile) return '';
      return formatFileSize(this.selectedFile.size);
    },

    handleFileSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.fileError = null;
      const category = getFileCategory(file.type);
      const limits = FILE_LIMITS[category];

      const allAllowedTypes = [
        ...FILE_LIMITS.image.types,
        ...FILE_LIMITS.video.types,
        ...FILE_LIMITS.audio.types,
        ...FILE_LIMITS.file.types
      ];

      if (!allAllowedTypes.includes(file.type)) {
        this.fileError = 'This file type is not supported';
        event.target.value = '';
        return;
      }

      if (file.size > limits.maxSize) {
        const maxMB = limits.maxSize / (1024 * 1024);
        this.fileError = `File is too large (max ${maxMB}MB for ${category}s)`;
        event.target.value = '';
        return;
      }

      this.clearPreview();
      this.selectedFile = file;

      if (category === 'image' || category === 'video') {
        this.previewUrl = URL.createObjectURL(file);
      }

      // Images get a description modal before they reach the composer preview.
      if (category === 'image') {
        this.openAltTextModal(file);
      }
    },

    // ─────────────────────────────────────────
    // "Add image" description modal
    // ─────────────────────────────────────────
    openAltTextModal(file) {
      this.imageAltText = '';
      this.imageAltTextFr = '';
      this.altTextError = null;
      this.altTextModalOpen = true;
      this.generateAltTextForImage(file);
    },

    // Cancel discards the pending attachment entirely.
    cancelAltTextModal() {
      this.altTextModalOpen = false;
      this.clearFile();
    },

    // Confirm keeps the attachment and drops back to the composer, where the
    // user can still add accompanying message text before sending.
    confirmAltTextModal() {
      this.altTextModalOpen = false;
    },

    async generateAltTextForImage(file) {
      // Guards against a stale response landing after the image was swapped.
      const requestId = ++this.altTextRequestId;
      this.isGeneratingAltText = true;
      this.altTextError = null;
      try {
        const base64Image = await fileToBase64(file);
        const response = await this.$api({
          url: '/image_accessibility/image_alt_text',
          method: 'POST',
          data: { base64_image: base64Image, mime_type: file.type || 'image/jpeg' },
          headers: { 'Content-Type': 'application/json' }
        });
        if (requestId !== this.altTextRequestId) return;

        this.trackChat('alt text', 'generate');
        const result = response.data || {};
        this.imageAltText = result.en?.alt_text || result.fr?.alt_text || '';
        this.imageAltTextFr = result.fr?.alt_text || result.en?.alt_text || '';
      } catch (error) {
        if (requestId !== this.altTextRequestId) return;

        this.altTextError = error?.status === 429
          ? this.altTextErrorMessages.rateLimited
          : this.altTextErrorMessages.failed;
      } finally {
        if (requestId === this.altTextRequestId) this.isGeneratingAltText = false;
      }
    },

    clearFile() {
      this.clearPreview();
      this.clearRecordedAudio();
      this.selectedFile = null;
      this.fileError = null;
      this.altTextModalOpen = false;
      this.imageAltText = '';
      this.imageAltTextFr = '';
      this.altTextError = null;
      this.altTextRequestId += 1;
      this.isGeneratingAltText = false;
      const fileInput = this.$refs.fileInput;
      if (fileInput) fileInput.value = '';
    },

    clearPreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    async toggleEmojiPicker() {
      this.emojiPickerOpen = !this.emojiPickerOpen;
      if (this.emojiPickerOpen && !this._pickerEl) {
        try {
          await this.initEmojiPicker();
        } catch {
          this.emojiPickerOpen = false;
        }
      }
    },

    closeEmojiPicker() {
      this.emojiPickerOpen = false;
    },

    async initEmojiPicker() {
      this._pickerEl = await createEmojiPicker({
        onEmojiSelect: (emoji) => this.insertEmoji(emoji)
      });
      this.$refs.emojiPickerContainer.appendChild(this._pickerEl);
    },

    insertEmoji(emoji) {
      const el = this.$refs.messageTextarea;
      el.focus();
      insertNodeAtCursor(document.createTextNode(emoji.native), el);
    },

    sendMessage() {
      if (!this.canSend) return;

      this.dismissMentionSearchResults();
      this.isSending = true;
      this.closeEmojiPicker();

      const { content: messageContent, mentions } = this.serializeContent();
      const el = this.$refs.messageTextarea;

      if (this.selectedFile) {
        this.$dispatch('chat:send-attachment', {
          conversationId: this.activeConversationId,
          content: messageContent,
          file: this.selectedFile,
          altText: this.imageAltText.trim(),
          altTextFr: this.imageAltTextFr.trim(),
          waveform: this.recordedWaveform,
          duration: this.recordedDuration,
          replyTo: this.replyTo,
          mentions,
        });
        this.clearFile();
      } else {
        this.$dispatch('chat:send', {
          conversationId: this.activeConversationId,
          content: messageContent,
          replyTo: this.replyTo,
          mentions,
        });
      }

      el.innerHTML = '';
      this.content = '';
      this.resetMentionedUsers();
      this.isSending = false;
      this.replyTo = null;
      this.$nextTick(() => el.focus());
    },

    handleKeydown(event) {
      // Mention navigation takes priority in group conversations.
      if (this.isGroupConversation() && this.handleMentionKeydown(event)) return;

      if (event.key === 'Escape' && this.editingMessage) {
        event.preventDefault();
        this.cancelEdit();
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (event.shiftKey) {
          insertNodeAtCursor(document.createElement('br'), this.$refs.messageTextarea);
        } else {
          this.submitComposer();
        }
      }
    },

    // Called from @input on the contenteditable div.
    handleInput() {
      if (this.isGroupConversation()) {
        const { text, cursor } = this._getLogicalTextAndCursor();
        this.onChangeParseMentions(text, cursor);
      }
    },

    // Strip rich text on paste — insert as plain text only.
    handlePaste(event) {
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData('text/plain');
      insertNodeAtCursor(document.createTextNode(text), this.$refs.messageTextarea);
      if (this.isGroupConversation()) {
        const { text: logicalText, cursor } = this._getLogicalTextAndCursor();
        this.onChangeParseMentions(logicalText, cursor);
      }
    },

    // Override mentionMixin's searchUsers to filter results to group members only.
    searchUsers() {
      const conv = this.getConversation(this.activeConversationId);
      const memberIds = new Set((conv?.members || []).map((m) => String(m.user_id)));
      const mentionString = this.mentionString;

      this.searching = true;

      this.$api({ url: '/users/search', query: { name: mentionString, per_page: this.resultsQueryLimit } })
        .then((data) => {
          this.searching = false;
          if (mentionString !== this.mentionString) return;
          this.results = data.data.data.filter((u) => memberIds.has(String(u.id)));
          this.selectedResultIndex = -1;
        });
    },

    // Override mentionMixin hook — inserts a styled chip span into the contenteditable div.
    insertMentionResult(result) {
      this.addMentionedUser(result);
      const el = this.$refs.messageTextarea;

      const startPos = this._getDomPositionAt(this.mentionStartIndex);
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;

      // Select from the triggering '@' to the current cursor and replace with chip.
      const mentionRange = document.createRange();
      mentionRange.setStart(startPos.node, startPos.offset);
      mentionRange.setEnd(sel.getRangeAt(0).startContainer, sel.getRangeAt(0).startOffset);
      mentionRange.deleteContents();

      const chip = createMentionChip(result);
      mentionRange.insertNode(chip);

      // Space after chip gives the cursor a text node to land in.
      const space = document.createTextNode(' ');
      chip.parentNode.insertBefore(space, chip.nextSibling);

      const afterRange = document.createRange();
      afterRange.setStart(space, 1);
      afterRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(afterRange);

      el.focus();
    },

    // Walk the contenteditable DOM and produce { content, mentions } for the send event.
    // Chip spans become @{{uuid}} tokens; BRs become newlines; everything else is plain text.
    serializeContent() {
      const el = this.$refs.messageTextarea;
      let content = '';
      const mentions = [];

      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          content += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains('chat-mention-chip')) {
            const { uuid, name, userId } = node.dataset;
            content += `@{{${uuid}}}`;
            if (!mentions.some((m) => m.attributes.uuid === uuid)) {
              mentions.push({
                attributes: { uuid, name },
                links: { profile_link: `/users/${userId}` },
              });
            }
          } else if (node.tagName === 'BR') {
            content += '\n';
          } else {
            node.childNodes.forEach(walk);
          }
        }
      };

      el.childNodes.forEach(walk);
      return { content: content.trim(), mentions };
    },

    // Returns the logical plain text (chip spans replaced by '\0' so their '@' doesn't
    // trigger mention detection) and the cursor offset within that text.
    _getLogicalTextAndCursor() {
      const el = this.$refs.messageTextarea;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return { text: '', cursor: 0 };

      const range = sel.getRangeAt(0);
      let text = '';
      let cursor = null;

      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (cursor === null && node === range.startContainer) {
            cursor = text.length + range.startOffset;
          }
          text += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains('chat-mention-chip')) {
            if (cursor === null && (node === range.startContainer || node.contains(range.startContainer))) {
              cursor = text.length + 1;
            }
            text += '\0';
          } else if (node.tagName === 'BR') {
            if (cursor === null && node === range.startContainer) cursor = text.length + 1;
            text += '\n';
          } else {
            node.childNodes.forEach(walk);
          }
        }
      };

      el.childNodes.forEach(walk);
      return { text, cursor: cursor ?? text.length };
    },

    // Map a logical character index (from _getLogicalTextAndCursor) to a { node, offset }
    // DOM position for use with Range.setStart / Range.setEnd.
    _getDomPositionAt(logicalIndex) {
      const el = this.$refs.messageTextarea;
      let count = 0;
      let result = null;

      const walk = (node) => {
        if (result) return;
        if (node.nodeType === Node.TEXT_NODE) {
          const len = node.textContent.length;
          if (count + len > logicalIndex) {
            result = { node, offset: logicalIndex - count };
            return;
          }
          count += len;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains('chat-mention-chip') || node.tagName === 'BR') {
            count += 1;
          } else {
            node.childNodes.forEach(walk);
          }
        }
      };

      el.childNodes.forEach(walk);
      return result ?? { node: el, offset: el.childNodes.length };
    },
  };
}
